import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Input, Row, Tree } from 'antd'
import { debounce, filter, flatten, isEmpty, map, uniq } from 'lodash'
import PropTypes from 'prop-types'

import { usePrevious } from '../../../core/hooks'

import { filterTree } from '../../helpers'
import { nodePropType } from '../../prop-types'
import { isDbId, stringIncludes } from '../../utils'

const SearchableMetadataTree = ({
  showSearch,
  treeData,
  flatTreeData,
  onExpand,
  titleRender,
  expandedKeys,
  disabled,
  ...props
}) => {
  const [searchableTreeData, setSearchableTreeData] = useState(treeData)
  const [searchValue, setSearchValue] = useState('')
  const [nonSearchExpandNodes, setNonSearchExpandNodes] = useState([])

  const handleOnSearch = useCallback(
    ({ target: { value } }) => setSearchValue(value),
    [],
  )

  const handleDebouncedFilterSearchableTree = useMemo(
    () =>
      debounce(value => {
        const filteredBySearchValueTreeData = filterTree(treeData, node => {
          if (stringIncludes(value, "'")) {
            return (
              stringIncludes(node.name, value) ||
              stringIncludes(node.name, value.replaceAll("'", '’'))
            )
          }

          return stringIncludes(node.name, value)
        })

        if (!isEmpty(value)) {
          const nodesToExpand = uniq(
            flatten(
              map(
                filter(flatTreeData, node => {
                  if (stringIncludes(value, "'")) {
                    return (
                      stringIncludes(node.name, value) ||
                      stringIncludes(node.name, value.replaceAll("'", '’'))
                    )
                  }

                  return stringIncludes(node.name, value)
                }),
                'keyPath',
              ),
            ),
          )

          onExpand(nodesToExpand)
        }

        setSearchableTreeData(filteredBySearchValueTreeData)
      }, 300),
    [treeData, flatTreeData, onExpand],
  )

  const handleTitleRender = useCallback(
    node => titleRender(node, searchValue),
    [titleRender, searchValue],
  )

  const previousSearchValue = usePrevious(searchValue)
  const previousTreeData = usePrevious(treeData)

  useEffect(() => {
    if (previousSearchValue !== searchValue) {
      handleDebouncedFilterSearchableTree(searchValue)
    }
  }, [searchValue, handleDebouncedFilterSearchableTree, previousSearchValue])

  useEffect(() => {
    const isTreeDataChanged = previousTreeData !== treeData
    if (isTreeDataChanged) {
      const isSearchValuePresent = !isEmpty(searchValue)

      if (isSearchValuePresent) {
        const filteredBySearchValueTreeData = filterTree(treeData, node => {
          if (stringIncludes(searchValue, "'")) {
            return (
              stringIncludes(node.name, searchValue) ||
              stringIncludes(node.name, searchValue.replaceAll("'", '’')) ||
              !isDbId(node.id)
            )
          }

          return stringIncludes(node.name, searchValue) || !isDbId(node.id)
        })

        setSearchableTreeData(filteredBySearchValueTreeData)
      } else {
        setSearchableTreeData(treeData)
      }
    }
  }, [previousTreeData, searchValue, treeData])

  useEffect(() => {
    if (isEmpty(searchValue)) {
      if (isEmpty(previousSearchValue)) {
        setNonSearchExpandNodes(expandedKeys)
      } else {
        onExpand(nonSearchExpandNodes)
      }
    }
  }, [
    searchValue,
    previousSearchValue,
    setNonSearchExpandNodes,
    expandedKeys,
    onExpand,
    nonSearchExpandNodes,
  ])

  return (
    <>
      {showSearch && (
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Input.Search
              value={searchValue}
              placeholder="Search metadata"
              onChange={handleOnSearch}
            />
          </Col>
        </Row>
      )}

      <Tree
        selectable={false}
        onExpand={onExpand}
        titleRender={handleTitleRender}
        treeData={searchableTreeData}
        expandedKeys={expandedKeys}
        blockNode
        disabled={disabled}
        {...props}
      />
    </>
  )
}

SearchableMetadataTree.propTypes = {
  showSearch: PropTypes.bool,
  treeData: PropTypes.arrayOf(nodePropType),
  flatTreeData: PropTypes.arrayOf(nodePropType),
  onExpand: PropTypes.func,
  titleRender: PropTypes.func,
  expandedKeys: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
}

SearchableMetadataTree.defaultProps = {
  showSearch: false,
  treeData: [],
  flatTreeData: [],
  onExpand: undefined,
  titleRender: undefined,
  expandedKeys: [],
  disabled: false,
}

export default SearchableMetadataTree
